import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fabric} from 'fabric';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {SET_UPLOADED_IMAGE} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {useFabricImage} from '~/CreateFabricInvitation/hooks/useFabricImage';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {toastAlert} from '~library/atoms/Toast';
import {mexicanOpalWebText} from '~libSass/base/_exports.sass';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

/**
 * Hook for handling image upload functionality across the application
 * @param {Object} canvas - Fabric canvas instance
 * @returns {Object} - Object containing upload state and handler function
 */
export const useImageUpload = (canvas) => {
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();
  const eventId = useSelector(getEventId);

  const {
    centerAndScaleImage,
    createImageContainer,
    createImageClipPath,
    findUYOImageById,
    sendRectsToBack,
  } = useFabricImage(canvas);

  /**
   * Handles the image file upload to GCS
   * @param {File} file - The file to upload
   * @returns {Promise<Object>} - The uploaded image details
   */
  const uploadImage = async (file) => {
    if (!file) return null;

    if (file.size > 10 ** 7) {
      toastAlert('Please select an image less than 10MB.', {
        className: 'uyo_toast__error',
        title: 'This image is too large',
        type: 'error',
        icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      throw new Error('Please select an image less than 10MB.');
    }

    const gcsUploadArgs = {
      gcsPathId: GCS_PATH_IDS.anonymous_image,
      params: {
        event_id: eventId,
        image_id: '',
      },
      image: file,
    };

    return gcsUploadImage(gcsUploadArgs);
  };

  /**
   * Sets a new image on the canvas
   * @param {string} url - The image URL
   * @param {Object} options - Configuration options
   */
  const setImageOnCanvas = (url, options) => {
    const {id, callback, isMultipleUyo = false, backgroundImage = {}} = options;

    fabric.UYOImage.fromURL(url, (img) => {
      const uyoImage = findUYOImageById(id);
      if (!uyoImage) return;

      const originalObject = uyoImage._originalObject || uyoImage.toJSON();

      const scale = fabric.util.findScaleToCover(img, originalObject);
      img.scale(scale);

      if (isMultipleUyo || backgroundImage.src?.endsWith('.gif')) {
        // eslint-disable-next-line no-param-reassign
        img.clipPath = createImageClipPath(originalObject, img);
        img.objectCaching = false;
      }

      if (!canvas.getObjects().find((obj) => obj.type === 'rect' && obj.id === id)) {
        const rect = createImageContainer(originalObject, id);
        canvas.insertAt(rect, 0);
      }

      img.set({
        id,
        _originalObject: originalObject,
      });

      canvas.remove(uyoImage);
      canvas.insertAt(img, 1);

      centerAndScaleImage(img, originalObject, {
        makeSelectable: true,
        resetRotation: true,
        fireEvents: ['object:added'],
      });

      sendRectsToBack();

      if (callback) callback(img);
    });
  };

  /**
   * Main handler for image upload process
   * @param {File} file - The file to upload
   * @param {Object} options - Configuration options
   * @returns {Promise<Object|void>} - The uploaded image details or void
   */
  const handleImageUpload = async (file, options = {}) => {
    const {
      id,
      callback,
      resetInputRef = null,
      isMultipleUyo = false,
      backgroundImage = {},
    } = options;

    if (!file) return;

    try {
      setUploading(true);
      const {url, gcsPath, imageKey} = await uploadImage(file);

      setImageOnCanvas(url, {
        id,
        callback,
        isMultipleUyo,
        backgroundImage,
      });

      dispatch({
        type: SET_UPLOADED_IMAGE,
        payload: {url, gcsPath, imageKey},
      });

      // eslint-disable-next-line consistent-return
      return {url, gcsPath, imageKey};
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
      if (resetInputRef?.current) {
        resetInputRef.current.value = '';
      }
    }
  };

  return {
    uploading,
    handleImageUpload,
  };
};

/**
 * Stand-alone function for image upload to GCS (for backward compatibility)
 * @param {File} file - The file to upload
 * @param {string} eventId - The event ID
 * @returns {Promise<Object>} - The uploaded image details
 */
export const imageFileUpload = async (file, eventId) => {
  if (file.size > 10 ** 7) {
    toastAlert('Please select an image less than 10MB.', {
      className: 'uyo_toast__error',
      title: 'This image is too large',
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    throw new Error('Please select an image less than 10MB.');
  }

  const gcsUploadArgs = {
    gcsPathId: GCS_PATH_IDS.anonymous_image,
    params: {
      event_id: eventId,
      image_id: '',
    },
    image: file,
  };

  return gcsUploadImage(gcsUploadArgs);
};
